import {moment, resolveTimezone} from "@/src/modules/core/lib/moment";

import type {OnGuardWindow, OnGuardWindowFormValues, OnGuardWindowPayload} from "../types";

const ISO_DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;

export const ON_GUARD_ISO_DAYS = [
  {value: 1, label: "Monday"},
  {value: 2, label: "Tuesday"},
  {value: 3, label: "Wednesday"},
  {value: 4, label: "Thursday"},
  {value: 5, label: "Friday"},
  {value: 6, label: "Saturday"},
  {value: 7, label: "Sunday"},
] as const;

export function isoDayShortLabel(dayOfWeek: number): string {
  return ISO_DAY_LABELS[dayOfWeek - 1] ?? String(dayOfWeek);
}

function parseClockParts(time: string): [number, number, number] {
  const parts = time.trim().split(":");
  const hour = Number(parts[0] ?? 0);
  const minute = Number(parts[1] ?? 0);
  const second = Number(parts[2] ?? 0);
  return [hour, minute, second];
}

export function formatClockTime12(time: string): string {
  const [hour, minute] = parseClockParts(time);
  const period = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12}:${String(minute).padStart(2, "0")} ${period}`;
}

function isFullUtcDay(window: Pick<OnGuardWindow, "from_time_utc" | "to_time_utc">): boolean {
  return window.from_time_utc.startsWith("00:00") && window.to_time_utc.startsWith("00:00");
}

export function serializeWindowForApi(
  window: OnGuardWindowFormValues,
  companyTimezone?: string | null,
): OnGuardWindowPayload {
  const tz = resolveTimezone(companyTimezone);
  const [fh, fm, fs] = parseClockParts(window.from_time);
  const [th, tm, ts] = parseClockParts(window.to_time);

  const fromLocal = moment
    .tz(tz)
    .startOf("isoWeek")
    .isoWeekday(window.day_of_week)
    .set({hour: fh, minute: fm, second: fs, millisecond: 0});

  let toLocal = moment
    .tz(tz)
    .startOf("isoWeek")
    .isoWeekday(window.day_of_week)
    .set({hour: th, minute: tm, second: ts, millisecond: 0});

  if (!toLocal.isAfter(fromLocal)) {
    toLocal = toLocal.add(1, "day");
  }

  const fromUtc = fromLocal.clone().utc();
  const toUtc = toLocal.clone().utc();

  return {
    day_of_week: fromUtc.isoWeekday(),
    from_time_utc: fromUtc.format("HH:mm:ss"),
    to_time_utc: toUtc.format("HH:mm:ss"),
  };
}

export function parseWindowFromApi(
  window: Pick<OnGuardWindow, "day_of_week" | "from_time_utc" | "to_time_utc">,
  companyTimezone?: string | null,
): OnGuardWindowFormValues {
  const tz = resolveTimezone(companyTimezone);
  const [fh, fm, fs] = parseClockParts(window.from_time_utc);
  const [th, tm, ts] = parseClockParts(window.to_time_utc);

  const fromUtc = moment
    .utc()
    .startOf("isoWeek")
    .isoWeekday(window.day_of_week)
    .set({hour: fh, minute: fm, second: fs, millisecond: 0});

  let toUtc = moment
    .utc()
    .startOf("isoWeek")
    .isoWeekday(window.day_of_week)
    .set({hour: th, minute: tm, second: ts, millisecond: 0});

  if (!toUtc.isAfter(fromUtc)) {
    toUtc = toUtc.add(1, "day");
  }

  const fromLocal = fromUtc.clone().tz(tz);
  const toLocal = toUtc.clone().tz(tz);

  return {
    day_of_week: fromLocal.isoWeekday(),
    from_time: fromLocal.format("HH:mm"),
    to_time: toLocal.format("HH:mm"),
  };
}

export function formatWindowLabel(
  window: OnGuardWindow,
  companyTimezone?: string | null,
): string {
  if (window.status === 0) return "";

  if (isFullUtcDay(window)) {
    const local = parseWindowFromApi(window, companyTimezone);
    return `${isoDayShortLabel(local.day_of_week)} (all day)`;
  }

  const local = parseWindowFromApi(window, companyTimezone);
  return `${isoDayShortLabel(local.day_of_week)} ${formatClockTime12(local.from_time)} – ${formatClockTime12(local.to_time)}`;
}

export function formatAssigneeSchedule(
  windows: OnGuardWindow[],
  companyTimezone?: string | null,
): string {
  const active = windows.filter((w) => w.status === 1);
  if (!active.length) return "—";
  return active.map((w) => formatWindowLabel(w, companyTimezone)).join("; ");
}

export type OnGuardScheduleLine = {
  day: string;
  time: string;
};

export type OnGuardWeeklyScheduleLine = OnGuardScheduleLine & {
  dayOfWeek: number;
};

export type OnGuardFullWeekScheduleRow = {
  day: string;
  time: string;
  active: boolean;
};

function windowToScheduleLine(
  window: OnGuardWindow,
  companyTimezone?: string | null,
): OnGuardWeeklyScheduleLine {
  const local = parseWindowFromApi(window, companyTimezone);
  const day = isoDayShortLabel(local.day_of_week);

  if (isFullUtcDay(window)) {
    return {day, time: "All day", dayOfWeek: local.day_of_week};
  }

  return {
    day,
    time: `${formatClockTime12(local.from_time)} – ${formatClockTime12(local.to_time)}`,
    dayOfWeek: local.day_of_week,
  };
}

/** Active windows Mon→Sun in company local time. */
export function getAssigneeWeeklyScheduleLines(
  windows: OnGuardWindow[],
  companyTimezone?: string | null,
): OnGuardWeeklyScheduleLine[] {
  return windows
    .filter((w) => w.status === 1)
    .map((w) => windowToScheduleLine(w, companyTimezone))
    .sort((a, b) => a.dayOfWeek - b.dayOfWeek);
}

function formatDaySpan(lines: OnGuardWeeklyScheduleLine[]): string {
  if (lines.length === 0) return "";
  if (lines.length === 1) return lines[0].day;

  const isConsecutive = lines.every(
    (line, index) => index === 0 || line.dayOfWeek === lines[index - 1].dayOfWeek + 1,
  );

  if (isConsecutive && lines.length >= 2) {
    return `${lines[0].day}–${lines[lines.length - 1].day}`;
  }

  if (lines.length <= 4) {
    return lines.map((line) => line.day).join(", ");
  }

  return `${lines.length} days`;
}

/** Compact one-line summary for list cells (e.g. Mon–Fri · 8:00 AM – 5:00 PM). */
export function formatAssigneeScheduleSummary(
  windows: OnGuardWindow[],
  companyTimezone?: string | null,
): string {
  const lines = getAssigneeWeeklyScheduleLines(windows, companyTimezone);
  if (!lines.length) return "—";

  const uniqueTimes = new Set(lines.map((line) => line.time));

  if (uniqueTimes.size === 1 && lines.length > 1) {
    return `${formatDaySpan(lines)} · ${lines[0].time}`;
  }

  return lines.map((line) => `${line.day} ${line.time}`).join("; ");
}

/** All seven weekdays for read-only schedule detail (active days show times; others Off). */
export function getAssigneeFullWeekSchedule(
  windows: OnGuardWindow[],
  companyTimezone?: string | null,
): OnGuardFullWeekScheduleRow[] {
  const byDay = new Map(
    getAssigneeWeeklyScheduleLines(windows, companyTimezone).map((line) => [
      line.dayOfWeek,
      line,
    ]),
  );

  return ON_GUARD_ISO_DAYS.map(({value, label}) => {
    const line = byDay.get(value);
    return {
      day: label,
      time: line?.time ?? "Off",
      active: Boolean(line),
    };
  });
}

/** Today's on-guard windows in company local time — one row per window for compact UI. */
export function getAssigneeTodayScheduleLines(
  windows: OnGuardWindow[],
  companyTimezone?: string | null,
): OnGuardScheduleLine[] {
  const active = windows.filter((w) => w.status === 1);
  if (!active.length) return [];

  const tz = resolveTimezone(companyTimezone);
  const todayIso = moment.tz(tz).isoWeekday();

  const todayWindows = active.filter((w) => {
    const local = parseWindowFromApi(w, companyTimezone);
    return local.day_of_week === todayIso;
  });

  const targets = todayWindows.length > 0 ? todayWindows : active;

  return targets.map((w) => {
    const {day, time} = windowToScheduleLine(w, companyTimezone);
    return {day, time};
  });
}
